import React, { useState, useRef } from "react";
import {
  PlusIcon,
  MinusIcon,
  ChevronUpDownIcon,
} from "@heroicons/react/16/solid";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import Input from "../../../../componentLibrary/Input";
import Button from "../../../../componentLibrary/Button";
import Tooltip from "../../../../componentLibrary/Tooltip";
import { parsePreviousAnswers } from "../../utils/parsePreviousAnswers";

interface Content {
  label: string;
  [key: string]: string;
}

interface UserListComponentProps {
  content: Content;
  onChange: (val: string[], displayAs?: string) => void;
  value?: string[];
  allResponses?: Record<string, { value: string | string[] }>;
  convertPlaceholder: boolean;
}

export default function UserListComponent({
  content,
  onChange: onChangeValue,
  value,
  allResponses = {},
  convertPlaceholder,
}: UserListComponentProps) {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const processValueDisplayAs = (value: string[]) => {
    return value.map((item) => `• ${item}`).join("\n");
  };

  const onChange = (val: string[]) => {
    onChangeValue(val, processValueDisplayAs(val));
  };

  const getInitialPlaceholders = () => {
    const placeholders: string[] = [];
    let i = 1;
    while (content[`placeholder_${i}`] !== undefined) {
      placeholders.push(
        parsePreviousAnswers(content[`placeholder_${i}`] || "", allResponses),
      );
      i++;
    }
    return placeholders;
  };

  const [entries, setEntries] = useState<string[]>(() => {
    const placeholders = getInitialPlaceholders();
    if (value && value.length > 0) {
      const newEntries = Array(placeholders.length).fill("");
      value.forEach((val, i) => {
        if (i < placeholders.length) {
          newEntries[i] = val;
        }
      });
      return newEntries;
    }
    return Array(Math.max(placeholders.length, 1)).fill("");
  });

  const handleAddEntry = () => {
    const newEntries = [...entries, ""];
    setEntries(newEntries);
    const nonEmptyEntries = newEntries.filter((entry) => entry.trim() !== "");
    onChange(nonEmptyEntries.length > 0 ? nonEmptyEntries : [""]);
    setTimeout(() => {
      const lastIndex = newEntries.length - 1;
      inputRefs.current[lastIndex]?.focus();
    }, 0);
  };

  const handleRemoveLastEntry = () => {
    if (entries.length > 1) {
      const newEntries = entries.slice(0, -1);
      setEntries(newEntries);
      const nonEmptyEntries = newEntries.filter((entry) => entry.trim() !== "");
      onChange(nonEmptyEntries.length > 0 ? nonEmptyEntries : [""]);
    }
  };

  const handleEntryChange = (index: number, newValue: string) => {
    const newEntries = [...entries];
    newEntries[index] = newValue;
    setEntries(newEntries);
    const nonEmptyEntries = newEntries.filter((entry) => entry.trim() !== "");
    onChange(nonEmptyEntries.length > 0 ? nonEmptyEntries : [""]);
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddEntry();
    } else if (
      e.key === "Backspace" &&
      entries[index] === "" &&
      entries.length > 1
    ) {
      e.preventDefault();
      const newEntries = entries.filter((_, i) => i !== index);
      setEntries(newEntries);
      const nonEmptyEntries = newEntries.filter((entry) => entry.trim() !== "");
      onChange(nonEmptyEntries.length > 0 ? nonEmptyEntries : [""]);
      setTimeout(() => {
        const prevIndex = Math.max(0, index - 1);
        inputRefs.current[prevIndex]?.focus();
      }, 0);
    }
  };

  const onDragEnd = (result) => {
    const { source, destination } = result;
    if (!destination) return;

    const newEntries = Array.from(entries);
    const [removed] = newEntries.splice(source.index, 1);
    newEntries.splice(destination.index, 0, removed);

    setEntries(newEntries);
    const nonEmptyEntries = newEntries.filter((entry) => entry.trim() !== "");
    onChange(nonEmptyEntries.length > 0 ? nonEmptyEntries : [""]);
  };

  React.useEffect(() => {
    if (value && Array.isArray(value)) {
      setEntries(
        entries.length > value.length
          ? entries.map((_, i) => value[i] || "")
          : value,
      );
    }
  }, []);

  return (
    <div className="mt-4 px-4">
      <div
        className="font-bold"
        style={{
          wordBreak: "break-word",
          whiteSpace: "normal",
        }}
      >
        {parsePreviousAnswers(content.label, allResponses)}
      </div>
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="entries" direction="vertical">
          {(provided) => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
              className="space-y-3"
            >
              {entries.map((entry, index) => {
                const placeholders = getInitialPlaceholders();
                return (
                  <Draggable
                    key={index}
                    draggableId={`entry-${index}`}
                    index={index}
                  >
                    {(provided) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        className="flex items-center space-x-2"
                      >
                        <div className="flex-grow">
                          <Input
                            value={entry}
                            onChange={(e) =>
                              handleEntryChange(index, e.target.value)
                            }
                            onKeyDown={(e) => handleKeyDown(index, e)}
                            ref={(el) => (inputRefs.current[index] = el)}
                            placeholder={
                              placeholders[index] ||
                              placeholders[placeholders.length - 1] ||
                              "Enter text"
                            }
                            convertPlaceholder={convertPlaceholder}
                          />
                        </div>
                        <div
                          {...provided.dragHandleProps}
                          className="cursor-grab active:cursor-grabbing"
                        >
                          <ChevronUpDownIcon className="w-6 h-6 text-gray-400" />
                        </div>
                      </div>
                    )}
                  </Draggable>
                );
              })}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
      <div className="flex space-x-3 justify-end mt-3 items-center">
        <Button
          variant="default"
          size="sm"
          onClick={handleRemoveLastEntry}
          disabled={entries.length <= 1}
        >
          <MinusIcon className="w-4 h-4" />
        </Button>
        <Button size="sm" onClick={handleAddEntry}>
          <PlusIcon className="w-4 h-4" />
        </Button>
        <Tooltip
          position="left"
          icon={<InformationCircleIcon className="w-6 h-6" />}
          component={
            <div className="flex flex-col">
              <div className="flex items-center gap-2">
                <PlusIcon className="w-4 h-4" />
                <div>Add a new entry</div>
              </div>
              <div className="flex items-center gap-2">
                <MinusIcon className="w-4 h-4" />
                <div>Remove lowest entry</div>
              </div>
              <div className="flex items-center gap-2">
                <ChevronUpDownIcon className="w-4 h-4" />
                <div>Move items up and down</div>
              </div>
            </div>
          }
        />
      </div>
    </div>
  );
}
